import React, { useState } from "react";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import AdminSidebar, { ADMIN_TABS } from "../components/admin/AdminSidebar";
import Toast from "../components/shared/Toast";
import Spinner from "../components/shared/Spinner";
import { ADMIN_EMAIL } from "../config";

import {
  AdminOverview,
  AdminEpisodes,
  AdminBlog,
  AdminGuests,
  AdminLive,
  AdminBreakingNews,
  AdminAbout,
  AdminNewsletter,
  AdminContactMsgs,
} from "../pages/admin/AdminPages";

import "./AdminDashboard.css";

/* ── Login screen ── */
export function AdminLogin({ auth }) {
  const [pw,        setPw]        = useState("");
  const [err,       setErr]       = useState("");
  const [signing,   setSigning]   = useState(false);

  const submit = async () => {
    if (!pw) return setErr("Please enter your password.");
    setSigning(true);
    setErr("");
    try {
      await signInWithEmailAndPassword(auth, ADMIN_EMAIL, pw);
      // onAuthStateChanged in App.jsx handles the redirect automatically
    } catch (e) {
      const msg = e.code === "auth/wrong-password" || e.code === "auth/invalid-credential"
        ? "Incorrect password. Try again."
        : e.code === "auth/too-many-requests"
        ? "Too many attempts. Try again later."
        : "Sign-in failed. Check your connection.";
      setErr(msg);
    } finally {
      setSigning(false);
    }
  };

  return (
    <div className="login-wrap">
      <div className="login-box">
        <img
          src={require("../config").LOGO_URL}
          alt="Logo"
          className="login-logo"
        />
        <h2>Admin Panel</h2>
        <p>Talk News · Kedrick Scribner Network</p>

        {err && <div className="login-error">{err}</div>}

        <div className="field-group">
          <label className="field-label">Password</label>
          <input
            className="field-input"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !signing && submit()}
            placeholder="Enter admin password"
            autoComplete="current-password"
            disabled={signing}
          />
        </div>

        <button
          className="btn-submit"
          style={{ width: "100%", marginTop: "0.5rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem" }}
          onClick={submit}
          disabled={signing}
        >
          {signing ? <><Spinner size="sm" /> Signing in…</> : "Sign In"}
        </button>

        <p className="login-hint">
          Navigate to <code>yoursite.com/#/admin</code> to access this panel.
        </p>
      </div>
    </div>
  );
}

/* ── Dashboard ── */
export default function AdminDashboard({ db, auth, onExit }) {
  const [tab,         setTab]         = useState("overview");
  const [toast,       setToast]       = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const currentTab = ADMIN_TABS.find((t) => t.id === tab);

  const switchTab = (id) => {
    setTab(id);
    setSidebarOpen(false);
  };

  const handleExit = async () => {
    await signOut(auth);
    onExit();
  };

  return (
    <div className="admin-layout">
      {sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      <AdminSidebar
        tab={tab}
        setTab={switchTab}
        onExit={handleExit}
        isOpen={sidebarOpen}
      />

      <main className="admin-main">
        <div className="admin-header">
          <div className="admin-header-left">
            <button
              className="sidebar-toggle"
              onClick={() => setSidebarOpen((o) => !o)}
              aria-label="Toggle sidebar"
            >
              ☰
            </button>
            <div>
              <h1>{currentTab?.label}</h1>
              <p>Manage your show content</p>
            </div>
          </div>
          <button className="btn-back" onClick={handleExit}>
            ← Back to Site
          </button>
        </div>

        {tab === "overview"   && <AdminOverview    db={db} setTab={switchTab} showToast={setToast} />}
        {tab === "episodes"   && <AdminEpisodes    db={db} showToast={setToast} />}
        {tab === "blog"       && <AdminBlog        db={db} showToast={setToast} />}
        {tab === "guests"     && <AdminGuests      db={db} showToast={setToast} />}
        {tab === "live"       && <AdminLive        db={db} showToast={setToast} />}
        {tab === "breaking"   && <AdminBreakingNews db={db} showToast={setToast} />}
        {tab === "about"      && <AdminAbout       db={db} showToast={setToast} />}
        {tab === "newsletter" && <AdminNewsletter  db={db} />}
        {tab === "contact"    && <AdminContactMsgs db={db} />}
      </main>

      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </div>
  );
}
